import {
  Cursor,
  Element,
  ElementType,
  FixtureType,
  getCanvasHeight,
  getCanvasWidth,
  LineType,
  PageFormat,
  SymbolType,
} from '@ritabygglov/core';
import { BASE_VIEW_SCALE } from './constants';
import { DimensionType } from './helpers/dimensionIds';

export interface SelectionCoordinates {
  currentX: null | number;
  currentY: null | number;
  initialX: null | number;
  initialY: null | number;
  startX: null | number;
  startY: null | number;
  initialWidth: null | number;
  initialHeight: null | number;
  originElements: Element[];
  xOffset: number;
  yOffset: number;
  selectedAnchor: null | Corner | Edge | number;
  nextPointIndex: number;
}

export enum Corner {
  TopLeft = 'TopLeft',
  TopRight = 'TopRight',
  BottomLeft = 'BottomLeft',
  BottomRight = 'BottomRight',
}

export enum Edge {
  Top = 'Top',
  Right = 'Right',
  Bottom = 'Bottom',
  Left = 'Left',
}

export type ResizeAnchor = Corner | Edge;

export const isCorner = (value: unknown): value is Corner =>
  value === Corner.TopLeft ||
  value === Corner.TopRight ||
  value === Corner.BottomLeft ||
  value === Corner.BottomRight;

export const isEdge = (value: unknown): value is Edge =>
  value === Edge.Top ||
  value === Edge.Right ||
  value === Edge.Bottom ||
  value === Edge.Left;

export enum SelectionModes {
  None = 'None',
  Add = 'Add',
  Selected = 'Selected',
  Resizing = 'Resizing',
  Rotating = 'Rotating',
  Panning = 'Panning',
  QuickPanning = 'QuickPanning',
  TextEditing = 'TextEditing',
  MultiSelecting = 'MultiSelecting',
  Updating = 'Updating',
  DimensionDragging = 'DimensionDragging',
  ApexDragging = 'ApexDragging',
  RadiusDragging = 'RadiusDragging',
  GroupEditing = 'GroupEditing',
}

export interface DimensionDragInfo {
  elementId: string;
  dimensionType: DimensionType;
  segmentIndex?: number;
  startOffset: number;
  startMouseX: number;
  startMouseY: number;
}

export interface SelectionMode {
  type: SelectionModes;
  elementType?: ElementType;
  lineType?: LineType;
  symbolType?: SymbolType;
  fixtureType?: FixtureType;
  presetType?: PresetType;
  groupId?: string;
}

export enum PresetType {
  WATER_CLOSET_PLAN = 'WATER_CLOSET_PLAN',
  SINK_PLAN = 'SINK_PLAN',
  BATH_TUB_PLAN = 'BATH_TUB_PLAN',
  CHAIR_PLAN = 'CHAIR_PLAN',
  TABLE_AND_CHAIRS_PLAN = 'TABLE_AND_CHAIRS_PLAN',
}

// https://en.wikipedia.org/wiki/Natural_Color_System
export enum COLORS {
  transparent = 'transparent',
  white = '#ffffff',
  black = '#000000',
  gray = '#D3D3D3',
  blue = '#0087BD',
  red = '#C40233',
  yellow = '#FFD300',
  green = '#009F6B',
}

export enum TextAlign {
  Left = 'left',
  Center = 'center',
  Right = 'right',
}

/*
  Alternative 1: Have everything in an array. Need to keep updating how where elements exists in the array.
  Alternative 2: Sort before rendering based on some numeric value. This will be more expensive as the whiteboard grows.
  Alternative 3: Keep a separate sorting array with the ids, this way, its at least easier to just shuffle around the ids, but we still need to update the element with sort order.

  Flow of incoming elements.
  Flow 1: New elements gets added to the top.
  Flow 2: Sometimes we bring some elements to the top.
  
  The only time something can get out of hand is if an item is removed from the sorting array, but still exists.

  Its easier to move around ids in an array, then to store all elements in an array. Since we get the O(1) lookup with the object storage.
  Re-ordering happens rarely. If we could have an object s
  
*/

export interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
  scale: number;
  startPoint: { x: number; y: number };
  endPoint: { x: number; y: number };
}

export interface AppState {
  map: Blob | undefined;
  elements: {
    [id: string]: Element;
  };
  cursors: { [id: string]: Cursor };
}

export const initialState: AppState = {
  map: undefined,
  elements: {},
  cursors: {},
};

const SCALE = BASE_VIEW_SCALE;
const LANDSCAPE_WIDTH = 1600;
const PORTRAIT_WIDTH = 1131;

const getWidth = (pageFormat: PageFormat) => {
  switch (pageFormat) {
    case PageFormat.A3_LANDSCAPE:
      return LANDSCAPE_WIDTH;
    case PageFormat.A3_PORTRAIT:
      return PORTRAIT_WIDTH;
    case PageFormat.A4_LANDSCAPE:
      return LANDSCAPE_WIDTH;
    case PageFormat.A4_PORTRAIT:
      return PORTRAIT_WIDTH;
    default:
      return LANDSCAPE_WIDTH;
  }
};

export const initialViewBox = (
  window: Window,
  pageFormat: PageFormat
): ViewBox => {
  const width = getWidth(pageFormat);
  return {
    x: -(window.innerWidth * SCALE) / 2 + width / 2,
    y: -200,
    w: window.innerWidth * SCALE,
    h: window.innerHeight * SCALE,
    startPoint: { x: 0, y: 0 },
    endPoint: { x: 0, y: 0 },
    scale: SCALE,
  };
};

export const exportViewBox = (pageFormat: PageFormat): ViewBox => {
  return {
    x: 0,
    y: 0,
    w: getCanvasWidth(pageFormat),
    h: getCanvasHeight(pageFormat),
    startPoint: { x: 0, y: 0 },
    endPoint: { x: 0, y: 0 },
    scale: 1,
  };
};

export const initialSelectionCoordinates = {
  currentX: null,
  currentY: null,
  initialX: null,
  initialY: null,
  startY: null,
  startX: null,
  initialHeight: null,
  initialWidth: null,
  originElements: [],
  xOffset: 0,
  yOffset: 0,
  selectedAnchor: null,
  nextPointIndex: 0,
};

export interface ClientCoordinates {
  x: number;
  y: number;
}

export class SelectionModeHelper {
  static isNone(selectionMode: SelectionMode) {
    return Boolean(selectionMode.type === SelectionModes.None);
  }

  static isPanning({
    selectionMode,
    isSpacePressed,
  }: {
    selectionMode: SelectionMode;
    isSpacePressed: boolean;
  }) {
    return (
      Boolean(selectionMode.type === SelectionModes.Panning) ||
      selectionMode.type === SelectionModes.QuickPanning ||
      (SelectionModeHelper.isNone(selectionMode) && isSpacePressed)
    );
  }

  static isQuickPanning(selectionMode: SelectionMode) {
    return Boolean(selectionMode.type === SelectionModes.QuickPanning);
  }

  static isMultiSelecting(selectionMode: SelectionMode) {
    return Boolean(selectionMode.type === SelectionModes.MultiSelecting);
  }

  static isSelected(selectionMode: SelectionMode) {
    return Boolean(selectionMode.type === SelectionModes.Selected);
  }

  static isGroupEditing(selectionMode: SelectionMode) {
    return Boolean(selectionMode.type === SelectionModes.GroupEditing);
  }

  static isPolylineActive(selectionMode: SelectionMode) {
    return Boolean(selectionMode.elementType === ElementType.Polyline);
  }

  static isRectActive(selectionMode: SelectionMode) {
    return Boolean(selectionMode.elementType === ElementType.Rect);
  }

  static isAddingRect(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Rect &&
      selectionMode.type === SelectionModes.Add
    );
  }

  static isAddingText(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Text &&
      selectionMode.type === SelectionModes.Add
    );
  }

  static isTextActive(selectionMode: SelectionMode) {
    return Boolean(selectionMode.elementType === ElementType.Text);
  }

  static isTextEditing(selectionMode: SelectionMode) {
    return Boolean(selectionMode.type === SelectionModes.TextEditing);
  }

  static isAddingPolyline(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Polyline &&
      selectionMode.type === SelectionModes.Add
    );
  }

  static isAddingLine(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Polyline &&
      selectionMode.type === SelectionModes.Add &&
      selectionMode.lineType === LineType.Line
    );
  }

  static isAddingDimension(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Polyline &&
      selectionMode.type === SelectionModes.Add &&
      selectionMode.lineType === LineType.Dimension
    );
  }

  static isAddingSymbol(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Symbol &&
      selectionMode.type === SelectionModes.Add
    );
  }

  static isSymbolActive(selectionMode: SelectionMode) {
    return Boolean(selectionMode.elementType === ElementType.Symbol);
  }

  static isAddingFixture(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Fixture &&
      selectionMode.type === SelectionModes.Add
    );
  }
  static isFixtureActive(selectionMode: SelectionMode) {
    return Boolean(selectionMode.elementType === ElementType.Fixture);
  }

  static isAddingTriangle(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Triangle &&
      selectionMode.type === SelectionModes.Add
    );
  }

  static isTriangleActive(selectionMode: SelectionMode) {
    return Boolean(selectionMode.elementType === ElementType.Triangle);
  }

  static isAddingTrapezoid(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Trapezoid &&
      selectionMode.type === SelectionModes.Add
    );
  }

  static isTrapezoidActive(selectionMode: SelectionMode) {
    return Boolean(selectionMode.elementType === ElementType.Trapezoid);
  }

  static isAddingAngle(selectionMode: SelectionMode) {
    return Boolean(
      selectionMode.elementType === ElementType.Angle &&
      selectionMode.type === SelectionModes.Add
    );
  }
}

export enum Side {
  left = 'left',
  right = 'right',
}

export interface SnapCandidate {
  targetPoint: Coordinate;
  originPoint: Coordinate;
  distance: number;
  sourceLine?: LineSegment;
}

export interface Coordinate {
  x: number;
  y: number;
}

export interface Vector {
  x: number;
  y: number;
}

export interface LineSegment {
  start: Coordinate;
  end: Coordinate;
}

export interface SnapLine {
  extended: LineSegment;
  original: LineSegment;
}
